<script setup>
import {
  ref,
  reactive,
  defineEmits,
  onMounted,
  nextTick,
  getCurrentInstance,
} from 'vue'
import {
  focusNextField as focusNextFieldHelper,
  handleKeydownField as handleKeydownFieldHelper,
} from '@/helpers'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/error-handler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanService from '@/services/LaporanService'
import SupplierService from '@/services/SupplierService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanService()
const supplierService = new SupplierService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
// let isLoadingExportExcel = ref(false)
let form = reactive({
  date_from: new Date(),
  date_to: new Date(),
  supplier: null,
})
let submitted = ref(false)
let list_supplier = ref([])
let selectedSupplier = ref(null)

const vm = getCurrentInstance().proxy

function handleKeydownField(event, nextRef, selectAll = false) {
  handleKeydownFieldHelper(vm, event, nextRef, selectAll)
}

function focusNextField(refName, selectAll = false) {
  focusNextFieldHelper(vm, refName, selectAll)
}

const laterDate = (value) => {
  if (!value || !form.date_from) return true

  return (
    dayjs(value).startOf('day').valueOf() >=
    dayjs(form.date_from).startOf('day').valueOf()
  )
}

const rules = {
  date_from: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
  date_to: {
    required: helpers.withMessage(
      'Tanggal "s/d" harus diisi lebih dari atau sama dengan tanggal "dari".',
      required
    ),
    laterDate,
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {
  const res = await supplierService.get()
  list_supplier.value = res.data

  await nextTick()
  setTimeout(() => {
    focusNextField('supplier')
  }, 100)
})

async function searchSupplier(event) {
  const query = event?.query ?? ''
  try {
    const res = await supplierService.getList(query)
    list_supplier.value = res.data.filter((s) =>
      s.name.toLowerCase().includes(query.toLowerCase())
    )
  } catch (err) {
    errorHandler(err, 'Data Supplier', toast)
  }
}

async function onSelectSupplier() {
  form.supplier = selectedSupplier.value?.id || null
  list_supplier.value = []
  await nextTick()
  setTimeout(() => focusNextField('date_from'), 100)
}

function onExport(isFormValid) {
  isLoadingExport.value = true
  const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  const supplierId = form.supplier === null ? 0 : form.supplier

  submitted.value = true

  if (!isFormValid) {
    isLoadingExport.value = false
    return
  }

  laporanService
    .PromoPenjualan(startDate, endDate, supplierId)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-promo.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Laporan Promo Penjualan', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

async function resetForm() {
  submitted.value = false

  form.date_from = new Date()
  form.date_to = new Date()
  form.supplier = null

  selectedSupplier.value = null
  list_supplier.value = []

  v$.value.$reset()

  await nextTick()

  setTimeout(() => {
    const supplierRef = vm.$refs.supplier

    if (supplierRef?.$el) {
      const input = supplierRef.$el.querySelector('input')

      if (input) {
        input.focus()
        input.select()
      }
    }
  }, 100)
}
</script>

<template>
  <fx-report-container
    title="Laporan Promo Penjualan"
    :loading="isLoadingExport"
    @toggle="onToggle"
    @export="onExport"
  >
    <template #content>
      <form @submit.prevent="onExport(!v$.$invalid)">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-4">
            <AutoComplete
              ref="supplier"
              v-model="selectedSupplier"
              class="w-full"
              input-class="w-full"
              placeholder="Pilih Supplier"
              :suggestions="list_supplier"
              field="name"
              :class="{ 'p-invalid': false }"
              @complete="searchSupplier"
              @item-select="onSelectSupplier"
              @focus="searchSupplier({ query: '' })"
              @keydown="handleKeydownField($event, 'date_from')"
            />
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              ref="date_from"
              v-model="v$.date_from.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_from.$invalid && submitted,
              }"
              placeholder="Tanggal (dari)"
              class="w-full"
              @keydown="handleKeydownField($event, 'date_to')"
            />
            <small
              v-if="
                (v$.date_from.$invalid && submitted) ||
                v$.date_from.$pending.$response
              "
              class="p-error"
              >{{ v$.date_from.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              ref="date_to"
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
              @keydown="handleKeydownField($event, 'btnCetak')"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-3">
            <Button
              ref="btnCetak"
              :loading="isLoadingExport"
              type="submit"
              icon="pi pi-file-pdf"
              class="ml-2 mb-0 md:mb-2 xl:mb-0"
              label="Cetak"
            />
            <!-- <Button
              :loading="isLoadingExportExcel"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2"
              @click="onExportExcel(!v$.$invalid)"
            /> -->
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2 btn-2xl-mt-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
